<template>
  <div class="tooth-item">
    <div class="tooth-checkbox">
      <input type="checkbox"
             :checked="isSelected"
             @change="handleSelection">
    </div>
    <div class="tooth-number">{{ tooth.number }}</div>
    <div class="tooth-illustration"
         :class="toothConditionClass"
         @click="handleToothClick">
      <img :src="toothSvg" alt="Dente" class="tooth-svg" />
    </div>
    <div class="tooth-surfaces">
      <div class="surface-grid">
        <div class="surface tooltip tooltip-top"
             v-for="surface in toothSurfaces"
             :key="surface"
             :class="{ active: isSurfaceActive(surface) }"
             :data-tooltip="getSurfaceTooltip(surface)"
             @click.stop="handleSurfaceClick(surface)"></div>
      </div>
    </div>
  </div>
</template>

<script>
import toothHealthy from '@/assets/images/tooth/tooth-healthy.svg';
import toothCarious from '@/assets/images/tooth/tooth-carious.svg';
import toothRestored from '@/assets/images/tooth/tooth-restored.svg';
import toothCrown from '@/assets/images/tooth/tooth-crown.svg';
import toothRootCanal from '@/assets/images/tooth/tooth-root-canal.svg';
import toothMissing from '@/assets/images/tooth/tooth-missing.svg';

export default {
  name: 'ToothItem',
  props: {
    tooth: {
      type: Object,
      required: true,
    },
    toothSurfaces: {
      type: Array,
      default: () => ['Oclusal', 'Mesial', 'Lingual', 'Distal', 'Vestibular'],
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      toothHealthy,
      toothCarious,
      toothRestored,
      toothCrown,
      toothRootCanal,
      toothMissing,
    };
  },
  computed: {
    toothSvg() {
      switch (this.tooth.condition) {
        case 'healthy':
          return this.toothHealthy;
        case 'carious':
          return this.toothCarious;
        case 'restored':
          return this.toothRestored;
        case 'crown':
          return this.toothCrown;
        case 'canal':
          return this.toothRootCanal;
        case 'missing':
          return this.toothMissing;
        default:
          return this.toothHealthy;
      }
    },
    toothConditionClass() {
      return {
        [this.tooth.condition]: true,
        selected: this.isSelected,
      };
    },
  },
  methods: {
    handleSelection() {
      this.$emit('tooth-selected', this.tooth);
    },
    handleToothClick() {
      this.$emit('tooth-clicked', this.tooth);
    },
    handleSurfaceClick(surface) {
      this.$emit('surface-clicked', this.tooth, surface);
    },
    isSurfaceActive(surface) {
      return this.tooth.surfaces && this.tooth.surfaces.includes(surface);
    },
    getSurfaceTooltip(surface) {
      const tooltips = {
        Oclusal: 'Face oclusal - Superfície de mastigação',
        Mesial: 'Face mesial - Lado voltado para a linha média',
        Lingual: 'Face lingual - Lado interno (língua)',
        Distal: 'Face distal - Lado oposto à linha média',
        Vestibular: 'Face vestibular - Lado externo (bochecha)',
      };
      return tooltips[surface] || surface;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/scss/_variables";

.tooth-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;

  .tooth-checkbox {
    margin-bottom: 5px;

    input[type="checkbox"] {
      width: 16px;
      height: 16px;
    }
  }

  .tooth-number {
    font-size: 12px;
    font-weight: 700;
    color: $dark-color;
    margin-bottom: 8px;
  }

  .tooth-illustration {
    width: 40px;
    height: 40px;
    position: relative;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    justify-content: center;

    .tooth-svg {
      width: 40px;
      height: 40px;
    }

    &.selected {
      box-shadow: 0 0 0 2px $primary-color;
    }
  }

  .tooth-surfaces {
    .surface-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 2px;
      width: 20px;

      .surface {
        width: 8px;
        height: 8px;
        border: 1px solid $border-color-dark;
        background-color: $bg-color-light;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
          background-color: lighten($primary-color, 40%);
        }

        &.active {
          background: $primary-color;
          border-color: $primary-color;
        }
      }
    }
  }
}

</style>
